import { useEffect, useState } from 'react';
import { api } from '@/services/api';
import LoadingIndicator from '@/components/LoadingIndicator';

export default function HouseRoomsAvailability({
  houseId,
  currentRoomNumber,
  currentRoomType,
  excludeUserId,
  onSuggestRoom,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!houseId) {
      setData(null);
      return;
    }
    let cancel = false;
    setLoading(true);
    setError('');
    api
      .getHouseRoomsAvailability(houseId)
      .then((res) => {
        if (!cancel) setData(res);
      })
      .catch((e) => {
        if (!cancel) setError(e?.response?.data?.detail || 'No se pudo cargar disponibilidad');
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [houseId]);

  if (!houseId) return null;
  if (loading) return <LoadingIndicator compact label="Cargando disponibilidad…" />;
  if (error) return <p className="text-xs text-destructive">{error}</p>;
  if (!data) return null;

  let typedHint = null;
  const typed = (currentRoomNumber || '').trim();
  if (typed) {
    const r = data.rooms.find((x) => String(x.room_number) === typed);
    if (r) {
      const isCurrentRoom = excludeUserId && (r.names || []).length > 0;
      const occ = isCurrentRoom ? Math.max(0, r.occupants - 1) : r.occupants;
      const free = Math.max(0, r.capacity - occ);
      if (currentRoomType === 'exclusive') {
        typedHint = occ > 0
          ? { kind: 'error', text: `Habitación ${typed} ya está ocupada por ${r.names.join(', ')}. Las exclusivas son individuales.` }
          : { kind: 'ok', text: `Habitación ${typed} libre — se asignará como exclusiva.` };
      } else {
        typedHint = free > 0
          ? { kind: 'ok', text: `Habitación ${typed}: ${occ}/${r.capacity} ocupada · ${free} cupo(s) libre(s)` }
          : { kind: 'error', text: `Habitación ${typed} ya alcanzó su capacidad (${r.capacity}). No podés sumar otro huésped.` };
      }
    } else {
      typedHint = { kind: 'ok', text: `Habitación ${typed}: nueva, sin ocupantes registrados.` };
    }
  }

  return (
    <div className="space-y-2" data-testid="rooms-availability-panel">
      {typedHint && (
        <p
          className={`text-xs rounded-lg px-3 py-2 ${
            typedHint.kind === 'ok'
              ? 'bg-accent-mint/30 border border-accent-mint text-foreground'
              : 'bg-destructive/15 border border-destructive text-destructive'
          }`}
          data-testid="rooms-availability-hint"
        >
          {typedHint.text}
        </p>
      )}

      <div className="rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
            Disponibilidad — {data.house_name}
          </p>
          <p className="text-xs text-muted-foreground">
            {data.configured_rooms}/{data.total_rooms} habs. · {data.shared_room_capacity} p/comp.
          </p>
        </div>
        {data.rooms.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sin habitaciones registradas todavía.</p>
        ) : (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {data.rooms.map((r) => {
              const full = r.free_slots === 0;
              return (
                <li key={r.room_number}>
                  <button
                    type="button"
                    onClick={() => onSuggestRoom?.(r.room_number)}
                    disabled={full && currentRoomType !== 'exclusive'}
                    title={r.names.join(', ') || ''}
                    className={`w-full text-left px-2 py-1.5 rounded-md text-xs border transition-colors ${
                      full
                        ? 'bg-destructive/10 border-destructive/50 text-destructive cursor-not-allowed'
                        : 'bg-surface border-border hover:bg-muted'
                    }`}
                    data-testid={`room-availability-${r.room_number}`}
                  >
                    <span className="font-semibold">Hab. {r.room_number}</span>
                    <span className="ml-1 text-muted-foreground">
                      {r.is_exclusive ? '(excl.)' : `${r.occupants}/${r.capacity}`}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {data.remaining_room_slots > 0 && (
          <p className="text-[11px] text-muted-foreground mt-2">
            Podés crear hasta {data.remaining_room_slots} habitación(es) nueva(s) en esta casa.
          </p>
        )}
      </div>
    </div>
  );
}
